import {
  HubConnectionBuilder,
  HubConnectionState,
  type HubConnection,
} from "@microsoft/signalr";

export type LiveState =
  "connecting" | "connected" | "reconnecting" | "disconnected";

/**
 * Realtime connection to /hubs/live. Remembers subscribed topics and subscribes again after a reconnect,
 * so pages only say what they want to watch.
 */
export class LiveClient {
  private readonly connection: HubConnection;
  private readonly topics = new Map<string, number>();
  private readonly stateListeners = new Set<(state: LiveState) => void>();

  constructor(url = "/hubs/live") {
    this.connection = new HubConnectionBuilder()
      .withUrl(url)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .build();
    this.connection.onreconnecting(() => this.emit("reconnecting"));
    this.connection.onreconnected(async () => {
      this.emit("connected");
      await this.subscribeAll();
    });
    this.connection.onclose(() => this.emit("disconnected"));
  }

  async start(): Promise<void> {
    if (this.connection.state !== HubConnectionState.Disconnected) {
      return;
    }
    this.emit("connecting");
    await this.connection.start();
    this.emit("connected");
    // Pages may subscribe before the first connection is up; send what they asked for now.
    await this.subscribeAll();
  }

  private async subscribeAll(): Promise<void> {
    await Promise.all(
      [...this.topics.keys()].map((t) =>
        this.connection.invoke("Subscribe", t),
      ),
    );
  }

  /** Reference-counted: several components may watch the same topic. Returns the unsubscribe function. */
  async subscribe(topic: string): Promise<() => Promise<void>> {
    const count = this.topics.get(topic) ?? 0;
    this.topics.set(topic, count + 1);
    if (count === 0 && this.connection.state === HubConnectionState.Connected) {
      await this.connection.invoke("Subscribe", topic);
    }
    return async () => {
      const left = (this.topics.get(topic) ?? 1) - 1;
      if (left > 0) {
        this.topics.set(topic, left);
        return;
      }
      this.topics.delete(topic);
      if (this.connection.state === HubConnectionState.Connected) {
        await this.connection.invoke("Unsubscribe", topic);
      }
    };
  }

  on<T>(event: string, handler: (payload: T) => void): () => void {
    this.connection.on(event, handler);
    return () => this.connection.off(event, handler);
  }

  onState(listener: (state: LiveState) => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  private emit(state: LiveState): void {
    for (const listener of this.stateListeners) {
      listener(state);
    }
  }
}

export const connectionTopic = (connectionId: string): string =>
  `connection:${connectionId}`;

export const deviceTopic = (deviceId: string): string => `device:${deviceId}`;
